<template>
  <div class="operation-page">
    <div class="page-header">
      <div>
        <h1 class="page-title">入金確認データリセット</h1>
        <p class="page-lead">
          入金確認履歴と最新入金確認を削除し、顧客残高を初期化します。請求書本体と請求明細は更新・削除しません。
        </p>
      </div>
      <div class="header-actions">
        <button type="button" class="btn btn-outline-primary" :disabled="isLoading" @click="loadSummary">
          最新状態を確認
        </button>
        <router-link :to="{ name: 'Home' }" class="btn btn-outline-secondary">ホーム</router-link>
      </div>
    </div>

    <div v-if="errorMessage" class="alert alert-danger">
      {{ errorMessage }}
    </div>

    <section class="operation-section">
      <h2>操作内容</h2>
      <div class="table-responsive">
        <table class="table table-bordered operation-table">
          <thead class="table-dark">
            <tr>
              <th>区分</th>
              <th>対象</th>
              <th>処理</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>削除</td>
              <td><code>t_monthly_customer_payment_confirm_histories</code></td>
              <td>入金確認履歴を全件削除</td>
            </tr>
            <tr>
              <td>削除</td>
              <td><code>t_monthly_customer_payment_confirm_latests</code></td>
              <td>顧客・年月別の最新入金確認を全件削除</td>
            </tr>
            <tr>
              <td>初期化</td>
              <td><code>t_customer_balances</code></td>
              <td><code>balance</code> と <code>unconfirm_amount</code> を 0 に更新</td>
            </tr>
            <tr class="table-light">
              <td>対象外</td>
              <td><code>t_customer_monthly_billings</code>, <code>t_customer_monthly_billing_details</code></td>
              <td>請求書データは参照・更新・削除しない</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <section class="operation-section">
      <div class="section-header">
        <h2>現在の対象件数</h2>
        <span v-if="isLoading" class="status-text">確認中...</span>
      </div>

      <div v-if="summary" class="summary-grid">
        <div class="summary-item">
          <span>入金確認履歴</span>
          <strong>{{ formatNumber(summary.histories_count) }}</strong>
          <small>入金 {{ formatCurrency(summary.histories_payment_total) }}</small>
          <small>その他控除 {{ formatCurrency(summary.histories_other_deduction_total) }}</small>
        </div>
        <div class="summary-item">
          <span>最新入金確認</span>
          <strong>{{ formatNumber(summary.latests_count) }}</strong>
          <small>入金 {{ formatCurrency(summary.latests_payment_total) }}</small>
          <small>その他控除 {{ formatCurrency(summary.latests_other_deduction_total) }}</small>
        </div>
        <div class="summary-item">
          <span>顧客残高</span>
          <strong>{{ formatNumber(summary.balances_count) }}</strong>
          <small>非ゼロ {{ formatNumber(summary.nonzero_balances_count) }} 件</small>
        </div>
        <div class="summary-item">
          <span>差引残高合計</span>
          <strong>{{ formatCurrency(summary.balances_total) }}</strong>
          <small>初期化対象</small>
        </div>
        <div class="summary-item">
          <span>未確認金額合計</span>
          <strong>{{ formatCurrency(summary.unconfirm_amount_total) }}</strong>
          <small>初期化対象</small>
        </div>
      </div>

      <div v-else class="empty-state">
        最新状態を確認してください。
      </div>
    </section>

    <section class="operation-section danger-section">
      <h2>実行確認</h2>
      <div class="confirm-row">
        <label class="form-check-label">
          <input v-model="confirmed" class="form-check-input me-2" type="checkbox">
          請求書本体・請求明細を操作しないこと、入金確認と顧客残高のみをリセットすることを確認しました。
        </label>
      </div>
      <div class="confirm-row">
        <label class="form-label" for="confirmText">確認語</label>
        <input
          id="confirmText"
          v-model="confirmText"
          class="form-control confirm-input"
          type="text"
          placeholder="RESET と入力"
          autocomplete="off"
        >
      </div>
      <button
        type="button"
        class="btn btn-danger"
        :disabled="!canExecute || isExecuting"
        @click="executeReset"
      >
        {{ isExecuting ? 'リセット実行中...' : '入金確認データをリセット' }}
      </button>
    </section>

    <section v-if="lastResult" class="operation-section result-section">
      <h2>実行結果</h2>
      <div class="result-grid">
        <div>
          <span>削除した入金確認履歴</span>
          <strong>{{ formatNumber(lastResult.result.deleted_histories) }}</strong>
        </div>
        <div>
          <span>削除した最新入金確認</span>
          <strong>{{ formatNumber(lastResult.result.deleted_latests) }}</strong>
        </div>
        <div>
          <span>初期化した顧客残高</span>
          <strong>{{ formatNumber(lastResult.result.reset_balance_rows) }}</strong>
        </div>
      </div>
      <div class="backup-path">
        バックアップ: <code>{{ lastResult.backup_dir }}</code>
      </div>
    </section>
  </div>
</template>

<script>
import { computed, onMounted, ref, watchEffect } from 'vue';
import { useStore } from 'vuex';
import {
  executeCustomerPaymentConfirmReset,
  getCustomerPaymentConfirmResetSummary
} from '@/api/axios';

export default {
  name: 'CustomerPaymentConfirmReset',
  setup() {
    const store = useStore();
    const title = ref('入金確認データリセット');
    const summary = ref(null);
    const lastResult = ref(null);
    const errorMessage = ref('');
    const isLoading = ref(false);
    const isExecuting = ref(false);
    const confirmed = ref(false);
    const confirmText = ref('');

    watchEffect(() => {
      document.title = title.value;
    });

    const canExecute = computed(() => {
      return Boolean(summary.value) && confirmed.value && confirmText.value === 'RESET';
    });

    const loadSummary = async () => {
      errorMessage.value = '';
      isLoading.value = true;

      try {
        const response = await getCustomerPaymentConfirmResetSummary({ reset_balances: true });
        summary.value = response.data.summary_before;
      } catch (error) {
        errorMessage.value = resolveErrorMessage(error, '最新状態の取得に失敗しました。');
      } finally {
        isLoading.value = false;
      }
    };

    const executeReset = async () => {
      if (!canExecute.value || isExecuting.value) return;

      const accepted = window.confirm('入金確認データをリセットします。実行後はバックアップCSVからの復旧が必要です。実行しますか？');
      if (!accepted) return;

      errorMessage.value = '';
      isExecuting.value = true;

      try {
        const response = await executeCustomerPaymentConfirmReset({ reset_balances: true });
        lastResult.value = response.data;
        summary.value = response.data.summary_after;
        confirmed.value = false;
        confirmText.value = '';
        store.dispatch('setFlashMessage', { message: '入金確認データをリセットしました', type: 'success' });
      } catch (error) {
        errorMessage.value = resolveErrorMessage(error, 'リセット処理に失敗しました。');
      } finally {
        isExecuting.value = false;
      }
    };

    const resolveErrorMessage = (error, fallbackMessage) => {
      const serverMessage = error.response?.data?.errors?.[0] ||
        error.response?.data?.error ||
        error.response?.data?.message;
      if (serverMessage) return serverMessage;

      const status = error.response?.status;
      if (status === 404) {
        return 'APIのリセット機能が環境に反映されていません。Rails APIのデプロイと再起動を確認してください。';
      }
      if (status === 401 || status === 403) {
        return 'この操作を実行する権限がありません。admin@example.com のシステム管理者でログインしてください。';
      }
      if (status >= 500) {
        return 'API側でエラーが発生しました。Railsログを確認してください。';
      }
      if (!error.response) {
        return 'APIサーバーに接続できません。API URL、CORS、ネットワーク設定を確認してください。';
      }

      return fallbackMessage;
    };

    const formatNumber = (value) => {
      return new Intl.NumberFormat('ja-JP').format(Number(value || 0));
    };

    const formatCurrency = (value) => {
      return new Intl.NumberFormat('ja-JP', {
        style: 'currency',
        currency: 'JPY'
      }).format(Number(value || 0));
    };

    onMounted(loadSummary);

    return {
      title,
      summary,
      lastResult,
      errorMessage,
      isLoading,
      isExecuting,
      confirmed,
      confirmText,
      canExecute,
      loadSummary,
      executeReset,
      formatNumber,
      formatCurrency
    };
  }
};
</script>

<style scoped>
.operation-page {
  padding: 20px;
  color: #1f2933;
}

.page-header {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
  margin-bottom: 20px;
}

.page-title {
  font-size: 28px;
  font-weight: 700;
  margin-bottom: 8px;
}

.page-lead {
  margin: 0;
  color: #4b5563;
  line-height: 1.6;
}

.header-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.operation-section {
  border: 1px solid #d7dde5;
  border-radius: 8px;
  padding: 18px;
  margin-bottom: 16px;
  background: #fff;
}

.operation-section h2 {
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 14px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
}

.status-text {
  color: #4b5563;
}

.operation-table {
  margin-bottom: 0;
}

.operation-table td,
.operation-table th {
  vertical-align: middle;
}

.summary-grid,
.result-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
}

.summary-item,
.result-grid > div {
  border: 1px solid #d7dde5;
  border-radius: 8px;
  padding: 14px;
  min-height: 96px;
  background: #f9fafb;
}

.summary-item span,
.result-grid span {
  display: block;
  font-size: 13px;
  color: #4b5563;
  margin-bottom: 8px;
}

.summary-item strong,
.result-grid strong {
  display: block;
  font-size: 22px;
  line-height: 1.25;
}

.summary-item small {
  display: block;
  color: #687586;
  margin-top: 6px;
}

.danger-section {
  border-color: #d04f4f;
}

.confirm-row {
  margin-bottom: 14px;
}

.confirm-input {
  max-width: 280px;
}

.backup-path {
  margin-top: 12px;
  word-break: break-all;
}

.empty-state {
  color: #687586;
}

@media (max-width: 768px) {
  .page-header {
    flex-direction: column;
  }

  .header-actions {
    width: 100%;
    justify-content: flex-start;
  }
}
</style>
